import { Actions, Async, F1 } from '@client/new/helpers/types';
import _ from 'lodash';
import {
  useGetDirectMessageStatusQuery,
  useSetDirectMessagingMutation,
} from 'generated/graphql';
import toast from 'react-hot-toast';
import { useState } from 'react';
import { useInfluencer } from '@client/new/hooks/use-influencer';
import {
  hasPremiumFeaturesOTPIds,
  isSubscriptionCreator,
} from '@client/new/common/offering.helpers';

export type InboxViewModelParams = {
  influencerId: number;
};

export type InboxViewModel = {
  dispatch: InboxViewDispatcher;
  isMessagingEnabled: boolean;
  submitting: boolean;
  influencerId: number;
  hideDirectMessages: boolean;
};

export type InboxViewActionsMap = {
  submitEnable: null;
};

export type InboxViewActions = Actions<InboxViewActionsMap>;

export type InboxViewDispatcher = F1<InboxViewActions>;

export const useInboxViewModel = (
  p: InboxViewModelParams,
): Async<InboxViewModel> => {
  const [submitting, setSubmitting] = useState(false);
  const [setDirectMessagingMutation] = useSetDirectMessagingMutation();

  const { influencer } = useInfluencer({
    creatorId: p.influencerId,
  });

  const { data, loading, error, refetch } = useGetDirectMessageStatusQuery({
    fetchPolicy: 'network-only',
    variables: { id: p.influencerId },
    skip: !p.influencerId,
  });

  if (loading) {
    return {
      type: 'fetching',
    };
  }

  if (error) {
    return {
      type: 'fetch-error',
      value: 'Could not fetch data',
    };
  }

  const onEnableDirectMessaging = async () => {
    setSubmitting(true);
    try {
      await setDirectMessagingMutation({
        variables: {
          data: {
            influencerId: p.influencerId,
            isEnabled: true,
          },
        },
      });
      await refetch();
      toast.success('Direct Messaging is now available!');
    } catch (e) {
      toast.error('Something went wrong!');
    } finally {
      setSubmitting(false);
    }
  };

  const dispatch: InboxViewDispatcher = (action) => {
    switch (action.type) {
      case 'submitEnable':
        onEnableDirectMessaging();
        return null;
    }
  };

  const isMessagingEnabled = !!data?.creator?.customizations?.canDirectMessage;
  const hideDirectMessages =
    !isSubscriptionCreator(influencer?.offering) &&
    !hasPremiumFeaturesOTPIds(influencer?.customizations);

  return {
    type: 'fetched',
    value: {
      dispatch,
      isMessagingEnabled,
      submitting,
      influencerId: p.influencerId,
      hideDirectMessages,
    },
  };
};
